import { apiClient } from './api';

export enum NotificationEventType {
  LEAVE_REQUEST = 'LEAVE_REQUEST',
  LEAVE_APPROVED = 'LEAVE_APPROVED',
  LEAVE_REJECTED = 'LEAVE_REJECTED',
  PPE_CREATED = 'PPE_CREATED',
  PPE_UPDATED = 'PPE_UPDATED',
  SALARY_TRANSFER = 'SALARY_TRANSFER',
  CARD_USAGE = 'CARD_USAGE',
  CARD_TRANSFER = 'CARD_TRANSFER',
}

export enum NotificationStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
}

export const NotificationEventTypeLabels: Record<NotificationEventType, string> = {
  [NotificationEventType.LEAVE_REQUEST]: '휴가 결제 상신',
  [NotificationEventType.LEAVE_APPROVED]: '휴가 승인 완료',
  [NotificationEventType.LEAVE_REJECTED]: '휴가 반려',
  [NotificationEventType.PPE_CREATED]: 'PPE 신규 생성',
  [NotificationEventType.PPE_UPDATED]: 'PPE 수정',
  [NotificationEventType.SALARY_TRANSFER]: '급여 이체 파일 발송',
  [NotificationEventType.CARD_USAGE]: '법인카드 사용 내역 등록',
  [NotificationEventType.CARD_TRANSFER]: '법인카드 이체 파일 발송',
};

export const NotificationStatusLabels: Record<NotificationStatus, string> = {
  [NotificationStatus.PENDING]: '대기',
  [NotificationStatus.SENT]: '발송완료',
  [NotificationStatus.FAILED]: '실패',
};

export interface NotificationSettings {
  id: number;
  event_type: NotificationEventType;
  is_enabled: boolean;
  recipient_emails: string;
  email_subject: string;
  email_template: string;
  send_immediately: boolean;
  batch_schedule: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Notification {
  id: number;
  event_type: NotificationEventType;
  event_title: string;
  event_detail: Record<string, any>;
  recipient_email: string;
  sender_email: string;
  status: NotificationStatus;
  sent_at: Date | null;
  error_message: string | null;
  retry_count: number;
  reference_type: string | null;
  reference_id: number | null;
  triggered_by: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface UpdateNotificationSettingsDto {
  is_enabled?: boolean;
  recipient_emails?: string;
  email_subject?: string;
  email_template?: string;
  send_immediately?: boolean;
  batch_schedule?: string;
}

export interface NotificationListResponse {
  success: boolean;
  data: Notification[];
  total: number;
  page: number;
  totalPages: number;
}

export interface NotificationListParams {
  page?: number;
  limit?: number;
  eventType?: NotificationEventType;
  status?: NotificationStatus;
  startDate?: string;
  endDate?: string;
}

export const notificationApi = {
  // Settings API
  getAllSettings: async (): Promise<NotificationSettings[]> => {
    const response = await apiClient.get<{ success: boolean; data: NotificationSettings[] }>('/notifications/settings');
    return response.data;
  },

  getSettingsByEventType: async (eventType: NotificationEventType): Promise<NotificationSettings> => {
    const response = await apiClient.get<{ success: boolean; data: NotificationSettings }>(`/notifications/settings/${eventType}`);
    return response.data;
  },

  updateSettings: async (eventType: NotificationEventType, dto: UpdateNotificationSettingsDto): Promise<NotificationSettings> => {
    const response = await apiClient.put<{ success: boolean; data: NotificationSettings; message: string }>(`/notifications/settings/${eventType}`, dto);
    return response.data;
  },

  // Notifications History API
  getNotifications: async (params: NotificationListParams = {}): Promise<NotificationListResponse> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.eventType) queryParams.append('eventType', params.eventType);
    if (params.status) queryParams.append('status', params.status);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/notifications?${queryString}` : '/notifications';
    return apiClient.get<NotificationListResponse>(endpoint);
  },

  getNotificationById: async (id: number): Promise<Notification> => {
    const response = await apiClient.get<{ success: boolean; data: Notification }>(`/notifications/${id}`);
    return response.data;
  },

  retryNotification: async (id: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.post<{ success: boolean; message: string }>(`/notifications/${id}/retry`, {});
  },

  sendTestNotification: async (eventType: NotificationEventType, email: string): Promise<{ success: boolean; message: string; data: Notification }> => {
    return apiClient.post<{ success: boolean; message: string; data: Notification }>('/notifications/test', { eventType, email });
  },
};
